"use client"

import { useQuery } from "@tanstack/react-query"
import { Building2, Flame, PackageX, RefreshCw, ShieldCheck, TrendingUp, Users } from "lucide-react"

import { Button } from "@/common/components/ui/button"
import { cn as cnUtil } from "@/common/lib/utils"
import { Skeleton } from "@/common/components/ui/skeleton"
import { cn } from "@/common/lib/utils"
import { BAUSTEIN_BY_ID } from "@/modules/lernpfad/data/bausteine"
import type { LernpfadDemoState } from "@/app/demo/lernpfad/use-lernpfad-demo"
import { getPopulationSummary } from "@/lernpfad-population-client"
import CoverageChart from "./charts/coverage-chart"
import { SEQUENTIAL_VAR, STATUS_VAR } from "./charts/palette"
import RankedBarChart, { type RankedBarDatum } from "./charts/ranked-bar-chart"
import { SectionHeading, Stat } from "./ui-bits"
import WirkungPanel from "./wirkung/wirkung-panel"
import { Chip, ViewHeader } from "@/modules/lernpfad/components/ui-bits"

/**
 * The L&D cockpit — aggregates over the whole seeded population.
 *
 * This view's credibility rests on the data being real-shaped: the 1,960
 * employees carry the ACTUAL Migros Bank org structure (unit names, hierarchy,
 * distribution), so coverage percentages and unit rankings look like a bank
 * rather than like twelve invented rows.
 */
export default function HrView({ state }: { state: LernpfadDemoState }) {
    const { constraints, localizer, moodleConnected, connections, syncBoost, setSyncBoost } = state
    const { t } = localizer

    // Keyed on the constraints, so every lever change refetches the aggregate.
    const { data: summary, isError, isFetching } = useQuery({
        queryKey: ["lernpfad-summary", constraints, syncBoost],
        queryFn: () =>
            getPopulationSummary({
                ...constraints,
                ...(syncBoost ? { simulateSync: true } : {}),
            }),
        staleTime: Infinity,
        placeholderData: (previous) => previous,
    })

    // Pre-sync baseline, kept so the delta chips can name what the sync changed.
    const { data: baseline } = useQuery({
        queryKey: ["lernpfad-summary", constraints, false],
        queryFn: () => getPopulationSummary(constraints),
        staleTime: Infinity,
        enabled: syncBoost,
    })

    const syncDelta =
        syncBoost && summary && baseline
            ? baseline.withOpenObligation - summary.withOpenObligation
            : 0

    if (isError) {
        return <p className="text-sm text-destructive">{t("hr.loadError")}</p>
    }

    if (!summary) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-16 w-full" />
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                    {Array.from({ length: 4 }, (_, index) => (
                        <Skeleton key={index} className="h-24" />
                    ))}
                </div>
                <Skeleton className="h-64" />
            </div>
        )
    }

    const uncovered = summary.skillGaps.filter((gap) => gap.uncovered)

    const gapData: RankedBarDatum[] = summary.skillGaps.slice(0, 10).map((gap) => ({
        id: gap.skillId,
        label: localizer.skill(gap.skillId, gap.label),
        value: gap.affected,
        flagged: gap.uncovered,
        flagLabel: t("hr.noBaustein"),
    }))

    // The aggregate carries German titles (it is computed from the monolingual
    // catalog server-side); resolve each id back through the localizer so the
    // English view does not show a German course list.
    const demandData: RankedBarDatum[] = summary.demand.slice(0, 10).map((entry) => ({
        id: entry.bausteinId,
        label: (() => {
            const baustein = BAUSTEIN_BY_ID.get(entry.bausteinId)
            return baustein ? localizer.baustein(baustein).title : entry.title
        })(),
        value: entry.count,
        secondary: t("item.hours", { hours: localizer.num(entry.hours) }),
    }))

    const unitPeak = Math.max(...summary.byOrgUnit.map((unit) => unit.hours), 1)

    return (
        <div className="space-y-8">
            <ViewHeader
                title={t("hr.title")}
                subtitle={t("hr.subtitle", { count: localizer.num(summary.headcount) })}
                action={
                    moodleConnected && (
                        <div className="flex flex-col items-end gap-1.5">
                            <Button
                                size="sm"
                                variant={syncBoost ? "outline" : "default"}
                                disabled={isFetching}
                                onClick={() => setSyncBoost(true)}
                            >
                                <RefreshCw className={cnUtil("size-3.5", isFetching && "animate-spin")} />
                                {isFetching ? t("hr.syncing") : t("hr.syncNow")}
                            </Button>
                            {syncDelta > 0 && (
                                <Chip tone="good">
                                    {t("hr.syncDelta", { count: localizer.num(syncDelta) })}
                                </Chip>
                            )}
                        </div>
                    )
                }
            >
                {moodleConnected && (
                    <p className="mt-1 text-xs text-muted-foreground">
                        {t("src.hrLine", {
                            count: connections.moodle?.syncedCourses ?? 0,
                            sync: t("int.lastSync", { when: t("int.justNow") }),
                        })}
                    </p>
                )}
            </ViewHeader>

            {/* Headline */}
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Stat
                    icon={ShieldCheck}
                    label={t("hr.stat.coverage")}
                    value={`${Math.round(summary.mandatoryCoverage * 100)}%`}
                    hint={t("hr.stat.coverageHint")}
                    tone={summary.mandatoryCoverage < 0.6 ? "danger" : "good"}
                />
                <Stat
                    icon={Users}
                    label={t("hr.stat.open")}
                    value={localizer.num(summary.withOpenObligation)}
                    hint={t("hr.stat.openHint", { count: localizer.num(summary.withOverdue) })}
                    tone="warn"
                />
                <Stat
                    icon={TrendingUp}
                    label={t("hr.stat.effort")}
                    value={t("item.hours", { hours: localizer.num(summary.totalPlannedHours) })}
                    hint={t("hr.stat.effortHint", { years: Math.round(summary.totalPlannedHours / 1700) })}
                />
                <Stat
                    icon={PackageX}
                    label={t("hr.stat.catalogGaps")}
                    value={String(uncovered.length)}
                    hint={t("hr.stat.catalogGapsHint")}
                    tone={uncovered.length > 0 ? "warn" : "neutral"}
                />
            </div>

            {/* Coverage per obligation */}
            <section className="lp-card rounded-xl border bg-card p-5">
                <SectionHeading icon={ShieldCheck} title={t("hr.coverage")} />
                <CoverageChart
                    rows={summary.coverage}
                    t={t}
                    obligationLabel={localizer.obligationLabel}
                    obligationBasis={localizer.obligationBasis}
                    num={localizer.num}
                />
            </section>

            <div className="grid gap-6 lg:grid-cols-2">
                {/* Skill gaps */}
                <section className="lp-card rounded-xl border bg-card p-5">
                    <SectionHeading icon={Flame} title={t("hr.topGaps")} />
                    <RankedBarChart data={gapData} format={localizer.num} />

                    {uncovered.length > 0 && (
                        <div className="mt-4 rounded-lg border-l-4 border-l-destructive border-y border-r bg-destructive/5 p-3">
                            <p className="text-sm font-semibold">{t("hr.catalogGapTitle")}</p>
                            <p className="mt-1 text-sm text-muted-foreground">
                                {t("hr.catalogGapBody", {
                                    skills: uncovered
                                        .slice(0, 3)
                                        .map((gap) => localizer.skill(gap.skillId, gap.label))
                                        .join(", "),
                                    count: localizer.num(uncovered[0].affected),
                                })}
                            </p>
                        </div>
                    )}
                </section>

                {/* Demand */}
                <section className="lp-card rounded-xl border bg-card p-5">
                    <SectionHeading icon={TrendingUp} title={t("hr.demand")} />
                    <RankedBarChart data={demandData} format={localizer.num} />
                </section>
            </div>

            {/* By org unit */}
            <section className="lp-card rounded-xl border bg-card p-5">
                <SectionHeading icon={Building2} title={t("hr.byUnit")} />
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b text-left text-xs text-muted-foreground">
                                <th className="pb-2 pr-4 font-medium">{t("hr.col.unit")}</th>
                                <th className="pb-2 pr-4 text-right font-medium">
                                    {t("hr.col.headcount")}
                                </th>
                                <th className="pb-2 pr-4 font-medium">{t("hr.col.effort")}</th>
                                <th className="pb-2 text-right font-medium">{t("hr.col.overdue")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {summary.byOrgUnit.map((unit) => (
                                <tr key={unit.orgUnitLabel} className="border-b last:border-0">
                                    <td className="py-2 pr-4">{unit.orgUnitLabel}</td>
                                    <td className="py-2 pr-4 text-right tabular-nums text-muted-foreground">
                                        {unit.headcount}
                                    </td>
                                    <td className="w-[45%] py-2 pr-4">
                                        <div className="flex items-center gap-2">
                                            <div className="relative h-1.5 flex-1">
                                                <div
                                                    className="absolute inset-0 rounded-full"
                                                    style={{ background: STATUS_VAR.track }}
                                                />
                                                <div
                                                    className="absolute inset-y-0 left-0 rounded-full"
                                                    style={{
                                                        width: `${(unit.hours / unitPeak) * 100}%`,
                                                        background: SEQUENTIAL_VAR,
                                                    }}
                                                />
                                            </div>
                                            <span className="w-16 shrink-0 text-right tabular-nums">
                                                {t("item.hours", { hours: localizer.num(unit.hours) })}
                                            </span>
                                        </div>
                                    </td>
                                    <td
                                        className={cn(
                                            "py-2 text-right tabular-nums",
                                            unit.overdue > 0
                                                ? "font-medium text-destructive"
                                                : "text-muted-foreground",
                                        )}
                                    >
                                        {unit.overdue}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            <WirkungPanel state={state} summary={summary} />
        </div>
    )
}
